import {
  MatrixQuestion,
  MatrixQuestionCreateApiObject,
  MatrixQuestionResults,
  MatrixQuestionSearchParams,
  MatrixQuestionSearchResult,
  MatrixQuestionUpdateApiObject,
  QuestionFilterIds,
} from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { cacheKeyForDateRange, defaultChartQueryOptions } from "utils";

import { MatrixQuestionsAPI } from "api";

const queryKeys = {
  all: () => ["matrix-questions"] as const,
  detail: (id: number | string) => [...queryKeys.all(), id] as const,
  results: (id: number | string, { startDate, endDate, ...rest }: QuestionFilterIds) =>
    [...queryKeys.all(), "results", { id, ...rest, ...cacheKeyForDateRange({ startDate, endDate }) }] as const,
  search: ({ query, orderByTerm, orderTerm }: MatrixQuestionSearchParams) => [
    ...queryKeys.all(),
    "search",
    query,
    orderByTerm,
    orderTerm,
  ],
};

/**
 * Searches for matrix questions based on the provided parameters.
 * Uses MatrixQuestionsAPI.list to fetch the data.
 *
 * @param params - The search parameters including query, orderByTerm, and orderTerm.
 * @param options - Optional react-query options for customizing the query behavior.
 * @returns A react-query hook for fetching matrix question search results.
 */
export const useMatrixQuestionSearch = (
  params: MatrixQuestionSearchParams,
  options: UseQueryOptions<MatrixQuestionSearchResult[], unknown, MatrixQuestionSearchResult[]> = {},
) => {
  return useQuery<MatrixQuestionSearchResult[], unknown, MatrixQuestionSearchResult[]>(
    queryKeys.search(params),
    () => MatrixQuestionsAPI.list(params),
    options,
  );
};

/**
 * Fetches a Matrix Question by ID.
 */
export const useMatrixQuestion = <T extends MatrixQuestion>(
  id: string | number,
  options: UseQueryOptions<MatrixQuestion, unknown, T> = {},
) =>
  useQuery<MatrixQuestion, unknown, T>(queryKeys.detail(id), () => MatrixQuestionsAPI.get(id), {
    ...options,
  });

/**
 * Fetches the results for a Matrix Question.
 * See `MatrixQuestionsAPI.fetchResults` for more details.
 */
export const useMatrixQuestionResults = <T = MatrixQuestionResults>(
  matrixQuestion: MatrixQuestion,
  filterIds: QuestionFilterIds,
  options: UseQueryOptions<MatrixQuestionResults, unknown, T> = {},
) =>
  useQuery<MatrixQuestionResults, unknown, T>(
    queryKeys.results(matrixQuestion.id, filterIds),
    () => MatrixQuestionsAPI.fetchResults(matrixQuestion, filterIds),
    {
      ...defaultChartQueryOptions,
      ...options,
    },
  );

/**
 * Create a new Matrix Question.
 */
export const useMatrixQuestionCreate = (
  options: UseMutationOptions<MatrixQuestion, unknown, MatrixQuestionCreateApiObject> = {},
) => {
  return useMutation((data) => MatrixQuestionsAPI.create(data), options);
};

/**
 * Mutation to update an existing MatrixQuestion.
 */
export const useMatrixQuestionUpdate = (
  id: number,
  options: UseMutationOptions<MatrixQuestion, unknown, MatrixQuestionUpdateApiObject> = {},
) => {
  return useMutation((data) => MatrixQuestionsAPI.update(id, data), options);
};

type MatrixQuestionFavoriteToggleArgs = {
  id: string | number;
  /**
   * True to mark as favorite, false to remove from favorites.
   */
  isFavorite: boolean;
};

/**
 * Mutation to toggle the favorite status of a matrix question.
 */
export const useMatrixQuestionFavoriteToggle = (
  options: UseMutationOptions<boolean, unknown, MatrixQuestionFavoriteToggleArgs> = {},
) => {
  return useMutation(async ({ id, isFavorite }: MatrixQuestionFavoriteToggleArgs) => {
    isFavorite ? MatrixQuestionsAPI.favorite(id) : MatrixQuestionsAPI.unfavorite(id);
    return isFavorite;
  }, options);
};
